import React from "react"
import * as Yup from "yup"
import {Formik} from "formik"
import Dropdown from "../../../Address/Dropdown"
import TextField from "../../../Address/Textfield"
import Card from "../../../../containers/Card/Card"
import DateField from "../../../DateField/DateField"
import AddressComponent from "../../../Address/Address"
import {countries} from "../../../../resources/countries"
import {US_COUNTRY_CODE, usStates} from "../../../../resources/address"
import {
	IndividualApplication,
	IndividualSourceOfFunds,
	BusinessSourceOfFunds,
	IndividualAccountPurpose,
	BusinessAccountPurpose,
	IndividualTransactionVolume,
	SolePropTransactionVolume,
	Profession,
	BusinessIndustry,
	CustomerDueDiligence,
} from "../../../../resources/application"
import {Address, FullName, OkDocument, Phone} from "../../../../resources/common"
import LeftRightSlots from "../../../../containers/LeftRightSlots/LeftRightSlots"
import individualValidationSchema from "./reevaluateindividualValidationSchema"
import IdentificationTypeSelect from "../../../IdentificationTypeSelect/ApplicationIdentificationNumber"
import PhoneNumber from "../../../Address/PhoneNumber"
import {ApplicationResubmitForms} from "../../../../pages/ReevaluateApplication/ReevaluatePage"
import UsNexusFormField from "../UsNexusFormField"
import CountriesOfOperationFormField from "../CountriesOfOperationFormField"

export interface ReevaluatedIndividualApplicationData {
	fullName: FullName
	address: Address
	ssn?: string
	nationality?: string
	passport?: string
	matriculaConsular?: string
	dateOfBirth: string | Date
	identificationType: string
	ein?: string
	website?: string
	phone: Phone
	businessIndustry?: BusinessIndustry | ""
	sourceOfFunds?: IndividualSourceOfFunds | BusinessSourceOfFunds | ""
	sourceOfFundsDescription?: string
	accountPurpose?: IndividualAccountPurpose | BusinessAccountPurpose | ""
	accountPurposeDetail?: string
	transactionVolume?: IndividualTransactionVolume | SolePropTransactionVolume | ""
	transactionVolumeDescription?: string
	profession?: Profession | ""
	usNexus?: string[]
	isIncorporated?: boolean
	stateOfIncorporation?: string
	yearOfIncorporation?: string
	countriesOfOperation?: string[]
	businessDescription?: string
}

export default function ReevaluateIndividualForm(props: {
	application: OkDocument<IndividualApplication>
	submitCallback: (values: ReevaluatedIndividualApplicationData) => void
	formId: ApplicationResubmitForms
}) {
	const {application, submitCallback, formId} = props
	const {data: individual} = application
	const isSoleProp = individual.attributes.soleProprietorship

	const validationSchema = Yup.object().shape(individualValidationSchema)
	const customerDueDiligence = individual.attributes.customerDueDiligence
	const shouldShowExtendedDueDiligenceFields =
		(customerDueDiligence && customerDueDiligence.includes(CustomerDueDiligence.Version2026v1)) ?? false

	const initialFormValues = {
		soleProprietorship: isSoleProp,
		fullName: {
			first: individual.attributes.fullName.first ?? "",
			last: individual.attributes.fullName.last ?? "",
		},
		address: {
			street: individual.attributes.address?.street ?? "",
			street2: individual.attributes.address?.street2 ?? "",
			city: individual.attributes.address?.city ?? "",
			state: individual.attributes.address?.state ?? "",
			postalCode: individual.attributes.address?.postalCode ?? "",
			country: individual.attributes.address?.country ?? US_COUNTRY_CODE,
		},
		ssn: individual.attributes.ssn ?? "",
		nationality: individual.attributes.nationality ?? "US",
		passport: individual.attributes.passport ?? "",
		matriculaConsular: individual.attributes.matriculaConsular ?? "",
		dateOfBirth: individual.attributes.dateOfBirth ?? "",
		identificationType: individual.attributes.ssn
			? "ssn"
			: individual.attributes.matriculaConsular
			? "matriculaConsular"
			: "passport",
		ein: individual.attributes.ein,
		phone: individual.attributes.phone,
		website: individual.attributes.website,
		businessIndustry: individual.attributes.businessIndustry,
		sourceOfFunds: individual.attributes.sourceOfFunds,
		sourceOfFundsDescription: individual.attributes.sourceOfFundsDescription,
		accountPurpose: individual.attributes.accountPurpose,
		accountPurposeDetail: individual.attributes.accountPurposeDetail,
		transactionVolume: individual.attributes.transactionVolume,
		transactionVolumeDescription: individual.attributes.transactionVolumeDescription,
		profession: individual.attributes.profession,
		usNexus: individual.attributes.usNexus,
		isIncorporated: individual.attributes.isIncorporated,
		stateOfIncorporation: individual.attributes.stateOfIncorporation,
		yearOfIncorporation: individual.attributes.yearOfIncorporation,
		countriesOfOperation: individual.attributes.countriesOfOperation,
		businessDescription: individual.attributes.businessDescription,
	}

	return (
		<Card className="reevaluate-form" title="Individual Information">
			<Formik
				initialValues={initialFormValues}
				validationSchema={validationSchema}
				onSubmit={(values) => submitCallback(values)}
			>
				{({values, handleSubmit, handleChange, setFieldValue, handleBlur}) => {
					return (
						<form noValidate onSubmit={handleSubmit} id={formId}>
							<LeftRightSlots
								left={
									<>
										<TextField
											label="First Name"
											placeholder="Add first name"
											name="fullName.first"
											value={values.fullName.first}
											onChange={handleChange}
											onBlur={handleBlur}
										/>
										<TextField
											label="Last Name"
											placeholder="Add last name"
											name="fullName.last"
											value={values.fullName.last}
											onChange={handleChange}
											onBlur={handleBlur}
										/>

										<IdentificationTypeSelect
											value={values.identificationType}
											onChange={handleChange}
											label="Document Type"
										/>

										{values.identificationType === "matriculaConsular" && (
											<TextField
												label="Document ID #"
												placeholder="Enter Matricula Consular"
												name="matriculaConsular"
												value={values.matriculaConsular}
												onChange={(value) => {
													handleChange(value)
													setFieldValue("ssn", "")
													setFieldValue("passport", "")
												}}
												onBlur={handleBlur}
											/>
										)}

										{values.identificationType === "ssn" && (
											<>
												<Dropdown
													name="nationality"
													label="Nationality"
													value={values.nationality}
													onChange={handleChange}
													onBlur={handleBlur}
												>
													{Object.keys(countries).map((countryCode) => (
														<option key={countryCode} value={countryCode}>
															{countries[countryCode]}
														</option>
													))}
												</Dropdown>
												<TextField
													label="Document ID #"
													placeholder="Enter SSN"
													name="ssn"
													value={values.ssn}
													onChange={(value) => {
														handleChange(value)
														setFieldValue("matriculaConsular", "")
														setFieldValue("passport", "")
													}}
													onBlur={handleBlur}
												/>
											</>
										)}

										{values.identificationType === "passport" ? (
											<>
												<Dropdown
													name="nationality"
													label="Nationality"
													value={values.nationality}
													onChange={handleChange}
													onBlur={handleBlur}
												>
													{Object.keys(countries).map((countryCode) => (
														<option key={countryCode} value={countryCode}>
															{countries[countryCode]}
														</option>
													))}
												</Dropdown>

												<TextField
													label="Document ID #"
													placeholder="Enter Passport"
													name="passport"
													value={values.passport}
													onChange={(value) => {
														handleChange(value)
														setFieldValue("matriculaConsular", "")
														setFieldValue("ssn", "")
													}}
													onBlur={handleBlur}
												/>
											</>
										) : null}

										<DateField
											name="dateOfBirth"
											label="Birth Date"
											value={values.dateOfBirth as string}
											onChange={handleChange}
											onBlur={handleBlur}
										/>
										{application.data.attributes.soleProprietorship ? (
											<TextField
												label="EIN"
												placeholder="Enter EIN"
												name="ein"
												value={values.ein ?? ""}
												onChange={(value) => {
													handleChange(value)
												}}
												onBlur={handleBlur}
											/>
										) : null}
										<TextField
											label="Website"
											placeholder="Enter website"
											name="website"
											value={values.website ?? ""}
											onChange={(value) => {
												handleChange(value)
											}}
											onBlur={handleBlur}
										/>

										<PhoneNumber
											name="phoneNumber"
											label="Phone Number"
											countryCodeName="phone.countryCode"
											phoneNumberName="phone.number"
											countryCodeValue={values.phone.countryCode}
											phoneNumberValue={values.phone.number}
											countryCodeOnChange={(value) => {
												setFieldValue("phone.countryCode", value)
											}}
											phoneNumberOnChange={(value) => {
												setFieldValue("phone.number", value)
											}}
											countryCodeOnBlur={handleBlur}
											phoneNumberOnBlur={handleBlur}
											infoTooltipText="Enter the country code followed by the phone number."
										/>

										{shouldShowExtendedDueDiligenceFields && (
											<>
												{/* Source of Funds - different options for individual vs sole prop */}
												<Dropdown
													name="sourceOfFunds"
													label="Source Of Funds"
													value={values.sourceOfFunds ?? ""}
													onChange={handleChange}
													onBlur={handleBlur}
												>
													{values.sourceOfFunds ? <React.Fragment /> : <option value="" />}
													{isSoleProp
														? Object.entries(BusinessSourceOfFunds).map(([key, value]) => (
																<option key={key} value={key}>
																	{value}
																</option>
														  ))
														: Object.entries(IndividualSourceOfFunds).map(([key, value]) => (
																<option key={key} value={key}>
																	{value}
																</option>
														  ))}
												</Dropdown>

												<div className="double-column-field">
													<TextField
														label="Source Of Funds Description"
														placeholder="Enter Source Of Funds Description"
														name="sourceOfFundsDescription"
														value={values.sourceOfFundsDescription ?? ""}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</div>

												{/* Account Purpose - different options for individual vs sole prop */}
												<Dropdown
													name="accountPurpose"
													label="Account Purpose"
													value={values.accountPurpose ?? ""}
													onChange={handleChange}
													onBlur={handleBlur}
												>
													{values.accountPurpose ? <React.Fragment /> : <option value="" />}
													{isSoleProp
														? Object.entries(BusinessAccountPurpose).map(([key, value]) => (
																<option key={key} value={key}>
																	{value}
																</option>
														  ))
														: Object.entries(IndividualAccountPurpose).map(([key, value]) => (
																<option key={key} value={key}>
																	{value}
																</option>
														  ))}
												</Dropdown>

												<div className="double-column-field">
													<TextField
														label="Account Purpose Detail"
														placeholder="Enter Account Purpose Detail"
														name="accountPurposeDetail"
														value={values.accountPurposeDetail ?? ""}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</div>

												{/* Transaction Volume - different options for individual vs sole prop */}
												<Dropdown
													name="transactionVolume"
													label="Transaction Volume"
													value={values.transactionVolume ?? ""}
													onChange={handleChange}
													onBlur={handleBlur}
												>
													{values.transactionVolume ? <React.Fragment /> : <option value="" />}
													{isSoleProp
														? Object.entries(SolePropTransactionVolume).map(([key, value]) => (
																<option key={key} value={key}>
																	{value}
																</option>
														  ))
														: Object.entries(IndividualTransactionVolume).map(([key, value]) => (
																<option key={key} value={key}>
																	{value}
																</option>
														  ))}
												</Dropdown>

												<div className="double-column-field">
													<TextField
														label="Transaction Volume Description"
														placeholder="Enter Transaction Volume Description"
														name="transactionVolumeDescription"
														value={values.transactionVolumeDescription ?? ""}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</div>

												{/* Profession - Individual only (non-sole prop) */}
												{!isSoleProp && (
													<Dropdown
														name="profession"
														label="Profession"
														value={values.profession ?? ""}
														onChange={handleChange}
														onBlur={handleBlur}
													>
														{values.profession ? <React.Fragment /> : <option value="" />}
														{Object.entries(Profession).map(([key, value]) => (
															<option key={key} value={key}>
																{value}
															</option>
														))}
													</Dropdown>
												)}

												{/* Sole Prop only fields */}
												{isSoleProp && (
													<>
														<Dropdown
															name="businessIndustry"
															label="Business Industry"
															value={values.businessIndustry ?? ""}
															onChange={handleChange}
															onBlur={handleBlur}
														>
															{values.businessIndustry ? <React.Fragment /> : <option value="" />}
															{Object.entries(BusinessIndustry).map(([key, value]) => (
																<option key={key} value={key}>
																	{value}
																</option>
															))}
														</Dropdown>

														<Dropdown
															name="isIncorporated"
															label="Is Incorporated"
															value={
																values.isIncorporated === true ? "yes" : values.isIncorporated === false ? "no" : ""
															}
															onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
																const val = e.target.value
																setFieldValue("isIncorporated", val === "yes" ? true : val === "no" ? false : undefined)
															}}
															onBlur={handleBlur}
														>
															<option value="" />
															<option value="yes">Yes</option>
															<option value="no">No</option>
														</Dropdown>

														<Dropdown
															name="stateOfIncorporation"
															label="State Of Incorporation"
															value={values.stateOfIncorporation ?? ""}
															onChange={handleChange}
															onBlur={handleBlur}
														>
															{values.stateOfIncorporation ? <React.Fragment /> : <option value="" />}
															{Object.entries(usStates).map(([key, value]) => (
																<option key={key} value={key}>
																	{value}
																</option>
															))}
														</Dropdown>

														<TextField
															label="Year Of Incorporation"
															placeholder="Enter Year Of Incorporation"
															name="yearOfIncorporation"
															value={values.yearOfIncorporation ?? ""}
															onChange={handleChange}
															onBlur={handleBlur}
														/>

														<UsNexusFormField
															value={values.usNexus}
															onChange={(val) => setFieldValue("usNexus", val)}
															onBlur={handleBlur}
														/>

														<CountriesOfOperationFormField
															value={values.countriesOfOperation}
															onChange={(val) => setFieldValue("countriesOfOperation", val)}
														/>

														<div className="double-column-field">
															<TextField
																label="Business Description"
																placeholder="Enter Business Description"
																name="businessDescription"
																value={values.businessDescription ?? ""}
																onChange={handleChange}
																onBlur={handleBlur}
															/>
														</div>
													</>
												)}
											</>
										)}
									</>
								}
								right={
									<AddressComponent
										closed={false}
										namePrefix="address."
										street={values.address.street}
										street2={values.address.street2}
										city={values.address.city}
										country={values.address.country || ""}
										state={values.address.state}
										postalCode={values.address.postalCode}
										isUsOnlyAddress={false}
										handleChange={handleChange}
										handleBlur={handleBlur}
										setFieldValue={setFieldValue}
									/>
								}
							/>
						</form>
					)
				}}
			</Formik>
		</Card>
	)
}
